import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, IconButton, Box, CircularProgress, Paper, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, FormControl, InputLabel, Select, TablePagination, InputAdornment,
  Tooltip, Snackbar, Alert, Chip, Divider, TableSortLabel
} from '@mui/material';
import {
  Refresh, Edit, Search, PersonAdd, Shield, GppGood, History,
  DeleteOutline, WarningAmber, AddBusiness
} from '@mui/icons-material';

import { theme } from '../theme/themeStore';

// Import the Stepper Component we created
import { CreateCompanyModal } from './CreateCompanyModal';

// ─── SORTING ───
// Sortable columns: key = what we sort by, defaultDir = direction on first click
const SORT_COLUMNS = {
  full_name:     { label: 'USER',        defaultDir: 'asc' },
  role:          { label: 'SYSTEM ROLE', defaultDir: 'asc' },
  status:        { label: 'STATUS',      defaultDir: 'asc' },
  last_login_at: { label: 'LAST LOGIN',  defaultDir: 'desc' }, // most recent first
};

const sortValue = (user, key) => {
  if (key === 'last_login_at') return user.last_login_at ? new Date(user.last_login_at).getTime() : null;
  return (user[key] ?? '').toString().toLowerCase() || null;
};

const compareUsers = (a, b, key, dir) => {
  const va = sortValue(a, key);
  const vb = sortValue(b, key);
  // Empty values (e.g. "Never" logged in) always go to the bottom
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const cmp = typeof va === 'number' ? va - vb : va.localeCompare(vb, undefined, { numeric: true });
  return dir === 'asc' ? cmp : -cmp;
};

const headCellSx = { fontWeight: 800, color: '#64748B' };
const sortLabelSx = {
  '&.MuiTableSortLabel-root:hover': { color: '#2F727C' },
  '&.Mui-active': { color: '#2F727C' },
  '&.Mui-active .MuiTableSortLabel-icon': { color: '#2F727C !important' },
};

export const ConsoleUserRegistry = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('full_name');
  const [order, setOrder] = useState('asc');

  // Modals State
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteConfirmOpen] = useState(false);

  // User Form State
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ full_name: '', email: '', role: 'EDITOR', status: 'ACTIVE', password: '' });
  const [saving, setSaving] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [feedback, setFeedback] = useState({ open: false, msg: '', severity: 'success' });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://console.dreamtec.io/console-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      setOrder(SORT_COLUMNS[key].defaultDir);
    }
    setPage(0);
  };

  const handleOpenUserDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({ full_name: user.full_name, email: user.email, role: user.role, status: user.status, password: '' });
    } else {
      setEditingUser(null);
      setFormData({ full_name: '', email: '', role: 'EDITOR', status: 'ACTIVE', password: '' });
    }
    setUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingUser
        ? `https://console.dreamtec.io/console-users/${editingUser.id}`
        : `https://console.dreamtec.io/console-users`;

      const response = await fetch(url, {
        method: editingUser ? 'PATCH' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error("Failed to save user");

      setFeedback({ open: true, msg: `User ${editingUser ? 'updated' : 'created'} successfully`, severity: 'success' });
      setUserModalOpen(false);
      fetchUsers();
    } catch (e) {
      setFeedback({ open: true, msg: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://console.dreamtec.io/console-users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to remove user");
      setFeedback({ open: true, msg: "User removed successfully", severity: 'success' });
      fetchUsers();
    } catch (e) {
      setFeedback({ open: true, msg: e.message, severity: 'error' });
    } finally {
      setSaving(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Search, then sort (sorting covers all matching users, not just the current page)
  const sortedUsers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return users
      .filter(u => u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
      .sort((a, b) => compareUsers(a, b, orderBy, order) || compareUsers(a, b, 'full_name', 'asc'));
  }, [users, searchQuery, orderBy, order]);

  // Keep the page valid after deletes / searching
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(sortedUsers.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [sortedUsers.length, rowsPerPage, page]);

  const renderSortHeader = (key, extraSx = {}) => (
    <TableCell sx={{ ...headCellSx, ...extraSx }} sortDirection={orderBy === key ? order : false}>
      <TableSortLabel
        active={orderBy === key}
        direction={orderBy === key ? order : SORT_COLUMNS[key].defaultDir}
        onClick={() => handleSort(key)}
        sx={sortLabelSx}
      >
        {SORT_COLUMNS[key].label}
      </TableSortLabel>
    </TableCell>
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#2F727C' }} /></Box>;

  return (
    <Box>
      {/* ─── HEADER ─── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2, mt: 1}}>
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1A3A3F',letterSpacing: -0.5, }}>
                User Management
            </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
          />

          {/* NEW COMPANY BUTTON */}
          {/* <Button
            variant="outlined"
            startIcon={<AddBusiness />}
            onClick={() => setCompanyModalOpen(true)}
            color="primary"
            sx={{  fontWeight: 800, textTransform: 'none'} }
          >
            New Company
          </Button> */}

          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => handleOpenUserDialog()}
            color="primary"
            sx={{  fontWeight: 800, textTransform: 'none'} }
          >
            Add User
          </Button>

        </Stack>
      </Stack>

      {/* ─── USER TABLE ─── */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '16px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#F8FAFC' }}>
            <TableRow>
              {renderSortHeader('full_name', { py: 2 })}
              {renderSortHeader('role')}
              {renderSortHeader('status')}
              {renderSortHeader('last_login_at')}
              <TableCell align="right" sx={headCellSx}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                  {searchQuery ? 'No users match your search.' : 'No users yet.'}
                </TableCell>
              </TableRow>
            )}
            {sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>{user.full_name}</Typography>
                  <Typography variant="caption" sx={{ color: '#2F727C', fontFamily: 'monospace' }}>{user.email}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    size="small"
                    icon={<Shield sx={{ fontSize: '12px !important' }} />}
                    sx={{ fontWeight: 800, fontSize: '0.65rem', bgcolor: 'rgba(47, 114, 124, 0.08)', color: '#2F727C' }}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: user.status === 'ACTIVE' ? '#10b981' : '#cbd5e1' }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{user.status}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <History sx={{ fontSize: 14 }} />
                        {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}
                    </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton size="small" onClick={() => handleOpenUserDialog(user)} sx={{ color: '#2F727C', bgcolor: '#F0F9FA' }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => { setUserToDelete(user); setDeleteConfirmOpen(true); }} sx={{ color: '#d32f2f', bgcolor: '#fff5f5' }}><DeleteOutline fontSize="small" /></IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sortedUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      {/* ─── MODALS ─── */}

      {/* 1. Create Company Stepper (The one we built) */}
      <CreateCompanyModal
        open={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        onSuccess={() => setFeedback({ open: true, msg: 'Company and Gateway config created!', severity: 'success' })}
      />

      {/* 2. Admin User Modal */}
      <Dialog open={userModalOpen} onClose={() => setUserModalOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GppGood sx={{ color: '#2F727C' }} /> {editingUser ? 'Update Admin Access' : 'Create System Admin'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField label="Full Name" fullWidth value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} />
            <TextField label="Email Address" fullWidth value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
            <TextField label="Password" type="password" fullWidth value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder={editingUser ? "Leave blank to keep" : ""} />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={formData.role} label="Role" onChange={(e) => setFormData({...formData, role: e.target.value})}>
                <MenuItem value="ADMIN">Super Admin</MenuItem>
                <MenuItem value="EDITOR">Standard User</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setUserModalOpen(false)} color="error">Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained" disabled={saving} color="primary">
            {saving ? <CircularProgress size={20} /> : (editingUser ? 'Update' : 'Grant Access')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3. Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteConfirmOpen(false)} PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmber sx={{ color: theme.palette.error.main }} /> Confirm Revocation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">Remove access for <b>{userToDelete?.full_name}</b>? This action is permanent.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="error" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={feedback.open} autoHideDuration={3000} onClose={() => setFeedback({...feedback, open: false})}>
        <Alert severity={feedback.severity} sx={{ fontWeight: 700, borderRadius: '10px' }}>{feedback.msg}</Alert>
      </Snackbar>
    </Box>
  );
};